"use client";

import { useEffect } from "react";
import { BetaPageLayout } from "@zapier/design-system";
import {
  AutomationsLayout,
  ConnectionsSdkCredentialsLayout,
  FoldersLayout,
  useAssetSidebar,
  WhatsNewAnnouncement,
} from "@zapier/shared";
import { ConnectAppsModalHost } from "@zapier/connections";
import { buildLoginUrl, SessionStatus, useSession } from "@zapier/identity";
import { usePathname } from "next/navigation";
import styles from "./layout.module.css";

export default function AssetsLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { status } = useSession();
  const isSignedOut = status === SessionStatus.Unauthenticated;

  // SessionProvider skips its own redirect when ssohint is "anonymous", so
  // this layout owns the redirect directly instead.
  useEffect(() => {
    if (isSignedOut) {
      window.location.href = buildLoginUrl();
    }
  }, [isSignedOut]);

  const { showAssetsSidebar, isLoading } = useAssetSidebar();
  const pathname = usePathname();

  const pageContent = (
    <BetaPageLayout className={styles.assetPageLayout}>
      <WhatsNewAnnouncement />
      {children}
    </BetaPageLayout>
  );

  // Covers both /app/assets/connections and its nested
  // /app/assets/connections/sdk-credentials route.
  const isConnectionsRoute = pathname?.startsWith("/app/assets/connections");

  const content = (() => {
    //prevent flash of incorrect layout
    if (isLoading) return;

    // Redirecting away (see effect above) — don't flash the signed-out
    // empty-state children while navigation is in flight.
    if (isSignedOut) return;

    // Connections/SDK credentials use their own sidebar (App connections /
    // SDK credentials switcher) instead of the asset-type sidebar below, so
    // this is checked before showAssetsSidebar rather than folded into it,
    // otherwise pageContent's padding (meant for single-column content) would
    // wrap the whole two-pane layout instead of just its content pane.
    if (isConnectionsRoute) {
      return (
        <ConnectionsSdkCredentialsLayout>
          {pageContent}
        </ConnectionsSdkCredentialsLayout>
      );
    }

    if (!showAssetsSidebar) {
      return pageContent;
    }

    const isFavorites = pathname === "/app/assets/favorites";
    const isFolders = pathname?.startsWith("/app/assets/folders");

    if (isFavorites) {
      return pageContent;
    }

    if (isFolders) {
      return <FoldersLayout>{pageContent}</FoldersLayout>;
    }

    return <AutomationsLayout>{pageContent}</AutomationsLayout>;
  })();

  // Mount the reconnect host once, OUTSIDE the layout-branch conditionals.
  // `useAssetSidebar` resolves async, so the layout swaps branches after the
  // initial render; if the host lived inside a branch that swap would unmount
  // and remount it, re-opening the modal. A single stable mount opens it once.
  return (
    <>
      <ConnectAppsModalHost />
      {content}
    </>
  );
}
